import { ReactNode, useEffect, useRef, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { adminNavLinks } from "@/mocks/admin";

type Props = {
  children: ReactNode;
  title: string;
  subtitle?: string;
  action?: ReactNode;
};

export default function AdminLayout({ children, title, subtitle, action }: Props) {
  const { user, ready, signOut } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!ready) return;
    if (!user || user.role !== "admin") {
      navigate("/admin/login", { replace: true });
    }
  }, [ready, user, navigate]);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileNavOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileNavOpen]);

  if (!ready || !user || user.role !== "admin") {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="flex items-center gap-3 text-foreground-600">
          <i className="ri-loader-4-line text-2xl animate-spin"></i>
          <span className="text-sm">Loading admin panel…</span>
        </div>
      </div>
    );
  }

  const initials = `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`.toUpperCase();

  return (
    <div className="min-h-screen bg-background-100 flex">
      {/* Sidebar */}
      <aside
        className={`fixed lg:sticky top-0 left-0 z-50 h-screen w-72 max-w-[85vw] bg-foreground-950 text-background-50 flex flex-col transition-transform duration-300 ${
          mobileNavOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
      >
        <div className="px-6 py-6 flex items-center justify-between">
          <Link to="/admin" className="flex items-center gap-2 group">
            <span className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-500">
              <i className="ri-leaf-line text-lg text-background-50"></i>
            </span>
            <div className="leading-tight">
              <p className="font-heading text-base font-semibold tracking-tight">PureNest</p>
              <p className="text-[10px] uppercase tracking-[0.18em] text-background-50/60">
                Admin console
              </p>
            </div>
          </Link>
          <button
            onClick={() => setMobileNavOpen(false)}
            className="lg:hidden w-9 h-9 flex items-center justify-center rounded-full bg-background-50/10 text-background-50 cursor-pointer"
            aria-label="Close menu"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        <div className="px-4 pb-4">
          <div className="relative">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-sm text-background-50/50"></i>
            <input
              type="text"
              placeholder="Search bookings, people…"
              className="w-full bg-background-50/10 placeholder:text-background-50/40 text-sm text-background-50 rounded-lg pl-9 pr-3 py-2 outline-none focus:bg-background-50/15"
            />
          </div>
        </div>

        <nav className="flex-1 px-4 space-y-1 overflow-y-auto">
          <p className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-[0.18em] text-background-50/40">
            Manage
          </p>
          {adminNavLinks.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              end={link.end}
              onClick={() => setMobileNavOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-primary-500 text-background-50"
                    : "text-background-50/75 hover:bg-background-50/10 hover:text-background-50"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <span
                    className={`w-8 h-8 flex items-center justify-center rounded-md ${
                      isActive ? "bg-background-50/15" : "bg-background-50/5"
                    }`}
                  >
                    <i className={`${link.icon} text-base`}></i>
                  </span>
                  {link.label}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="px-4 pb-6 border-t border-background-50/10 pt-4 mt-4">
          <div ref={menuRef} className="relative">
            <button
              onClick={() => setMenuOpen((v) => !v)}
              className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-background-50/5 transition-colors cursor-pointer"
            >
              <span className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-500 text-background-50 text-xs font-semibold">
                {initials}
              </span>
              <div className="flex-1 text-left min-w-0">
                <p className="text-sm font-medium truncate">
                  {user.firstName} {user.lastName}
                </p>
                <p className="text-[11px] text-background-50/60 truncate">Owner · Admin</p>
              </div>
              <i className="ri-more-2-fill text-background-50/60"></i>
            </button>

            {menuOpen && (
              <div className="absolute bottom-full mb-2 left-0 right-0 bg-background-50 border border-background-200 rounded-xl overflow-hidden">
                <Link
                  to="/dashboard"
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-foreground-800 hover:bg-background-100"
                >
                  <i className="ri-user-line text-foreground-600"></i> Customer dashboard
                </Link>
                <Link
                  to="/"
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-foreground-800 hover:bg-background-100"
                >
                  <i className="ri-home-line text-foreground-600"></i> Back to site
                </Link>
                <button
                  onClick={() => {
                    signOut();
                    navigate("/admin/login");
                  }}
                  className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-foreground-800 hover:bg-background-100 cursor-pointer"
                >
                  <i className="ri-logout-box-r-line text-foreground-600"></i> Sign out
                </button>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Mobile overlay */}
      {mobileNavOpen && (
        <div
          className="fixed inset-0 z-40 bg-foreground-950/50 lg:hidden"
          onClick={() => setMobileNavOpen(false)}
        ></div>
      )}

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Mobile top bar */}
        <header className="lg:hidden sticky top-0 z-30 bg-background-100/90 backdrop-blur-md border-b border-background-200">
          <div className="px-4 py-3 flex items-center gap-3">
            <button
              onClick={() => setMobileNavOpen(true)}
              className="w-10 h-10 flex items-center justify-center rounded-full bg-background-50 border border-background-200 text-foreground-800 cursor-pointer flex-shrink-0"
              aria-label="Open menu"
            >
              <i className="ri-menu-line text-lg"></i>
            </button>
            <Link to="/admin" className="flex items-center gap-2 flex-1 min-w-0">
              <span className="w-8 h-8 flex items-center justify-center rounded-full bg-foreground-950 flex-shrink-0">
                <i className="ri-leaf-line text-sm text-background-50"></i>
              </span>
              <div className="leading-tight min-w-0">
                <p className="font-heading text-sm font-semibold truncate">PureNest</p>
                <p className="text-[9px] uppercase tracking-widest text-foreground-600 truncate">
                  Admin
                </p>
              </div>
            </Link>
            <button
              className="w-10 h-10 flex items-center justify-center rounded-full bg-background-50 border border-background-200 text-foreground-700 cursor-pointer flex-shrink-0 relative"
              aria-label="Notifications"
            >
              <i className="ri-notification-3-line text-lg"></i>
              <span className="absolute top-2 right-2 w-2 h-2 rounded-full bg-accent-500"></span>
            </button>
          </div>
        </header>

        {/* Page header */}
        <div className="px-4 md:px-8 pt-5 md:pt-6 pb-2 md:pb-4 lg:sticky lg:top-0 lg:z-20 lg:bg-background-100/85 lg:backdrop-blur-md lg:border-b lg:border-background-200">
          <div className="flex items-start md:items-center gap-3 md:gap-4 flex-wrap">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="hidden md:inline-flex text-[10px] font-semibold uppercase tracking-[0.18em] text-primary-700 bg-primary-100 px-2 py-0.5 rounded-full">
                  Admin
                </span>
                <h1 className="font-heading text-xl md:text-2xl lg:text-[28px] font-semibold text-foreground-950 leading-tight">
                  {title}
                </h1>
              </div>
              {subtitle && (
                <p className="text-xs md:text-sm text-foreground-600">{subtitle}</p>
              )}
            </div>
            {action && <div className="flex-shrink-0 w-full sm:w-auto">{action}</div>}
          </div>
        </div>

        <main className="flex-1 px-4 md:px-8 py-4 md:py-8 pb-24 lg:pb-8">{children}</main>
      </div>

      {/* Mobile bottom nav */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-30 bg-background-50/95 backdrop-blur-md border-t border-background-200">
        <div className="grid grid-cols-5">
          {adminNavLinks.slice(0, 5).map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              end={link.end}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center gap-1 py-2.5 text-[10px] font-medium transition-colors ${
                  isActive ? "text-primary-700" : "text-foreground-600"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <span
                    className={`w-9 h-9 flex items-center justify-center rounded-full transition-colors ${
                      isActive ? "bg-primary-100" : ""
                    }`}
                  >
                    <i className={`${link.icon} text-lg`}></i>
                  </span>
                  <span className="truncate max-w-full px-1">{link.label}</span>
                </>
              )}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}